import { useEffect, useState } from "react";
import { create } from "zustand";

interface ViewerFile {
  url: string;
  mimeType: string;
  filename: string;
  /** Kept for "Save / share" — a File can be handed to the phone's share sheet. */
  file: File;
}

interface ViewerState {
  current: ViewerFile | null;
  show(bytes: Uint8Array, mimeType: string, filename: string): void;
  close(): void;
}

/**
 * Shared photos, videos and files open in this full-screen viewer, inside
 * TrueWave. They used to be opened in a new browser window via a blob: URL —
 * which an iPhone Home Screen app can't do, so nothing appeared (and, being
 * view-once, the attachment was already gone). The bytes live only in memory
 * while the viewer is open; closing it releases them.
 */
export const useViewer = create<ViewerState>((set, get) => ({
  current: null,
  show(bytes, mimeType, filename) {
    get().close();
    const type = mimeType || "application/octet-stream";
    const name = filename || "attachment";
    const file = new File([new Uint8Array(bytes).buffer as ArrayBuffer], name, { type });
    set({ current: { url: URL.createObjectURL(file), mimeType: type, filename: name, file } });
  },
  close() {
    const cur = get().current;
    if (cur) URL.revokeObjectURL(cur.url);
    set({ current: null });
  },
}));

export function AttachmentViewer() {
  const current = useViewer((s) => s.current);
  const close = useViewer((s) => s.close);
  const [shareError, setShareError] = useState<string | null>(null);

  useEffect(() => {
    setShareError(null);
    if (!current) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [current, close]);

  // Nothing of an attachment survives leaving the chat.
  useEffect(() => () => useViewer.getState().close(), []);

  if (!current) return null;
  const { url, mimeType, filename, file } = current;
  const isImage = mimeType.startsWith("image/");
  const isVideo = mimeType.startsWith("video/");
  const isPdf = mimeType === "application/pdf";
  const canShare = typeof navigator.canShare === "function" && navigator.canShare({ files: [file] });

  async function share() {
    try {
      await navigator.share({ files: [file], title: filename });
    } catch (err) {
      if ((err as { name?: string }).name !== "AbortError") setShareError("Couldn't open the share sheet.");
    }
  }

  return (
    <div className="viewer" role="dialog" aria-modal="true" aria-label={filename}>
      <div className="viewer-bar">
        <button className="viewer-close" onClick={close} aria-label="Close">
          ✕
        </button>
        <span className="viewer-name">{filename}</span>
        {canShare && (
          <button className="viewer-action" onClick={() => void share()}>
            Save / share
          </button>
        )}
      </div>
      <div className="viewer-body" onClick={(e) => e.target === e.currentTarget && close()}>
        {isImage && <img src={url} alt={filename} />}
        {isVideo && <video src={url} controls autoPlay playsInline />}
        {isPdf && <iframe src={url} title={filename} />}
        {!isImage && !isVideo && !isPdf && (
          <div className="viewer-file">
            <div className="viewer-file-icon">📄</div>
            <div>{filename}</div>
            {canShare ? (
              <button className="primary" onClick={() => void share()}>
                Open with…
              </button>
            ) : (
              <a className="primary-link" href={url} download={filename}>
                Download
              </a>
            )}
          </div>
        )}
        {shareError && <div className="error">{shareError}</div>}
      </div>
    </div>
  );
}
